import {
  Badge,
  Box,
  Flex,
  HStack,
  Input,
  SimpleGrid,
  Skeleton,
  Stack,
  Text,
} from "@chakra-ui/react";
import { useEffect, useMemo, useState } from "react";
import { Holiday } from "src/api/Holiday";
import Content from "src/components/Content";
import type { DataTableColumnDef as ColumnDef, DataTableCellContext as CellContext } from "src/components/DataTable";
import DataTable from "src/components/DataTable";
import { isWeekend } from "date-fns";
import WaitingSpinner from "@/components/WaitingSpinner";
import StatCard from "@/components/StatCard";
import SectionPanel from "@/components/SectionPanel";
import { Tag } from "@/components/ui/tag";
import ZurichFlag from "@/components/ZurichFlag";
import { countAllHolidays, countHolidaysOnWorkdays } from "./TimesheetData";
import { holidayService } from "@/service/HolidayService";
import YearNavigation from "./components/YearNavigation";
import CountrySelectController from "@/components/fields/CountrySelect";
import { useForm } from "react-hook-form";
import { useI18n } from "@/i18n/I18nContext";
import { useLocationDefaults } from "@/hooks/useLocationDefaults";
import { Alert } from "@/components/ui/alert";
import { Field } from "@/components/ui/field";
import { useAuthContext, useOrganization } from "@/auth/AuthContext";
import { OrgUtil } from "@/data/OrgUtil";
import type { CustomHoliday } from "@/api/CustomHoliday";
import { HolidayDetails } from "./components/HolidayDetails";
import { usePageRefresh } from "@/hooks/usePageRefresh";
import {
  LuBriefcaseBusiness,
  LuCalendarCheck,
  LuCalendarDays,
} from "react-icons/lu";

// not available LA, NP
const supportedHolidayCountries = [
  "CH",
  "AT",
  "HU",
  "GB",
  "DE",
  "FR",
  "IT",
  "ES",
  "SE",
  "KE",
  "UG",
  "US",
  "CA",
  "MZ",
  "BD"
];

const OverviewPublicHoliday = () => {
  const { location } = useLocationDefaults();
  const { user } = useAuthContext();
  const organization = useOrganization();
  const token = user?.token;
  const organizationId = user?.data.organizationId;
  const role = user?.data.role;
  const isAdmin = !!role && (OrgUtil.isRoleAdmin(role) || OrgUtil.isRoleRoot(role));
  const revision = usePageRefresh();
  const [rules, setRules] = useState<CustomHoliday[]>([]);
  const [rulesError, setRulesError] = useState(false);

  const [currentYear, setCurrentYear] = useState<number>(new Date().getFullYear());
  const [isLoading, setIsLoading] = useState(true);
  const [data, setData] = useState<Holiday[]>([]);
  const [error, setError] = useState<string | null>(null);
  const { language, t } = useI18n();
  const { control, register, watch, getValues, setValue } = useForm<{ country: string; city: string }>({
    values: { country: location.country, city: location.city ?? "" },
    resetOptions: { keepDirtyValues: true },
  });
  const country = watch("country");
  const city = watch("city").trim();
  const languageIsoCode = language.toUpperCase();
  const dateLocale = language === "en" ? "en-US" : "de-CH";
  const dateFormatter = useMemo(
    () =>
      new Intl.DateTimeFormat(dateLocale, {
        day: "2-digit",
        month: "short",
        year: "numeric",
      }),
    [dateLocale]
  );

  useEffect(() => {
    let isActive = true;

    setIsLoading(true);
    setData([]);
    setRules([]);
    setRulesError(false);
    setError(null);
    if (!country) return;
    const calendar = holidayService.holidays(currentYear, country, languageIsoCode, city);
    const customRules = token && organization?.id === organizationId && isAdmin
      ? holidayService.listCustom(currentYear).catch(() => {
        if (isActive) setRulesError(true);
        return [];
      }) : Promise.resolve<CustomHoliday[]>([]);
    Promise.all([calendar, customRules])
      .then(([holidays, custom]) => {
        if (isActive) {
          setData(holidays);
          setRules(custom);
        }
      })
      .catch((err) => {
        console.error(err?.message ?? err);
        if (isActive) {
          setData([]);
          setError(t("publicHolidays.errorText"));
        }
      })
      .finally(() => {
        if (isActive) {
          setIsLoading(false);
        }
      });

    return () => {
      isActive = false;
    };
  }, [currentYear, country, city, languageIsoCode, t, token, organizationId, organization?.id, isAdmin, revision]);

  const allHolidays = useMemo(() => countAllHolidays(data), [data]);
  const holidaysOnWorkdays = useMemo(() => countHolidaysOnWorkdays(data), [data]);

  const metricCards = useMemo(
    () => [
      {
        label: t("publicHolidays.total"),
        value: data.length,
        helpText: t("publicHolidays.totalHelp"),
        icon: LuCalendarDays,
        accent: "blue",
      },
      {
        label: t("publicHolidays.balance"),
        value: allHolidays,
        helpText: t("publicHolidays.balanceHelp"),
        icon: LuCalendarCheck,
        accent: "green",
      },
      {
        label: t("publicHolidays.workdays"),
        value: holidaysOnWorkdays,
        helpText: t("publicHolidays.workdaysHelp"),
        icon: LuBriefcaseBusiness,
        accent: "orange",
      },
    ],
    [allHolidays, data.length, holidaysOnWorkdays, t]
  );

  const columns: ColumnDef<Holiday>[] = useMemo(
    () => [
      {
        id: "date",
        header: t("publicHolidays.date"),
        accessorKey: "date",
        cell: (info: CellContext<Holiday, Date>) => {
          const day = new Date(info.getValue());
          const weekend = isWeekend(day);
          return (
            <Text
              color={weekend ? "green.fg" : "fg"}
              fontSize="sm"
              fontWeight={weekend ? "semibold" : "medium"}
              whiteSpace="nowrap"
            >
              {dateFormatter.format(day)}
            </Text>
          );
        },
      },
      {
        id: "day",
        header: t("publicHolidays.weekday"),
        accessorKey: "date",
        cell: (info: CellContext<Holiday, Date>) => {
          const day = new Date(info.getValue());
          const weekend = isWeekend(day);
          return (
            <Text
              color={weekend ? "green.fg" : "fg"}
              fontSize="sm"
              textTransform="capitalize"
              whiteSpace="nowrap"
            >
              {day.toLocaleDateString(dateLocale, { weekday: "long" })}
            </Text>
          );
        },
      },
      {
        id: "name",
        header: t("publicHolidays.description"),
        accessorKey: "name",
        cell: (info: CellContext<Holiday, string>) => {
          const weekend = isWeekend(new Date(info.row.original.date));
          return (
            <Stack gap="1">
              <Text color={weekend ? "green.fg" : "fg"} fontSize="sm" fontWeight="medium">
                {info.getValue()}
              </Text>
              <HolidayDetails holiday={info.row.original} rule={rules.find(rule => rule.id === info.row.original.customHolidayId)} />
            </Stack>
          );
        },
      },
      {
        id: "tags",
        header: t("publicHolidays.notes"),
        accessorFn: (row: Holiday): string[] => {
          const tags = new Array<string>();
          if (!row.nationwide) {
            tags.push("regional");
          }
          if (row.weekend) {
            tags.push("weekend");
          }
          if (row.lengthInDays < 1) {
            tags.push("halfDay");
          }
          return tags;
        },
        cell: (info: CellContext<Holiday, string[]>) => {
          const tags = info.getValue();
          return (
            <HStack gap="1" wrap="wrap">
              {tags.map((tag: string) => (
                <Tag
                  key={tag}
                  variant="surface"
                  size="sm"
                  colorPalette={tag === "weekend" ? "green" : "teal"}
                  fontSize="xs"
                  whiteSpace="nowrap"
                >
                  {t(`publicHolidays.${tag}`)}
                </Tag>
              ))}
            </HStack>
          );
        },
      },
    ],
    [dateFormatter, dateLocale, t, rules]
  );

  return (
    <Content title={t("publicHolidays.titleWithYear", { year: currentYear })}>
      <Stack gap="6" minW="0">
        <SectionPanel>
          <Flex
            align="stretch"
            direction={{ base: "column", lg: "row" }}
            gap={{ base: 4, lg: 6 }}
          >
            <Stack
              flex={{ base: "1", lg: "0 1 26rem" }}
              minW="0"
              gap="3"
              justify="center"
              align='center'
            >
              <SimpleGrid columns={{ base: 1, sm: 2 }} gap="3" w="full" minW="0">
                <CountrySelectController
                  control={control}
                  name="country"
                  readOnly={false}
                  countryCodes={Array.from(new Set([...supportedHolidayCountries, ...(country ? [country] : [])]))}
                  language={language}
                  label={t("signup.form.country")}
                  isClearable={false}
                  onValueChange={value => {
                    if (value !== getValues("country")) setValue("city", "", { shouldDirty: true });
                  }}
                />
                <Field label={t("signup.form.city")} minW="0">
                  <Input {...register("city")} autoComplete="address-level2" />
                </Field>
              </SimpleGrid>
              <YearNavigation
                currentYear={currentYear}
                setCurrentYear={setCurrentYear}
              />
            </Stack>

            <SimpleGrid
              columns={{ base: 1, md: 3 }}
              gap={{ base: 3, md: 4 }}
              flex="1"
              minW="0"
            >
              {metricCards.map((metric) => (
                <StatCard
                  key={metric.label}
                  label={metric.label}
                  value={metric.value}
                  helpText={metric.helpText}
                  icon={metric.icon}
                  accentColor={`${metric.accent}.600`}
                  iconBg={`${metric.accent}.50`}
                  isLoading={isLoading}
                />
              ))}
            </SimpleGrid>
          </Flex>
        </SectionPanel>

        <SectionPanel
          title={t("publicHolidays.listTitle")}
          actions={(
            <HStack gap="2" wrap="wrap">
              {isLoading && (
                <Badge variant="surface" colorPalette="blue" px="3" py="1">
                  <HStack gap="2">
                    <WaitingSpinner size="xs" />
                    <Text>{t("publicHolidays.loading")}</Text>
                  </HStack>
                </Badge>
              )}
              {country === "CH" && ["zurich", "zürich", "zuerich"].includes(city.toLowerCase()) && (
                <Badge variant="surface" colorPalette="blue" px="3" py="1">
                  <HStack gap="2">
                    <ZurichFlag />
                    <Text>{t("publicHolidays.zurich")}</Text>
                  </HStack>
                </Badge>
              )}
              <Badge variant="surface" colorPalette="green" px="3" py="1">
                {t("publicHolidays.totalCount", { count: data.length })}
              </Badge>
            </HStack>
          )}
        >
          {rulesError && <Alert role="alert" status="warning" title={t("publicHolidays.rulesError")} />}

          {error ? (
            <Alert
              status="error"
              colorPalette="red"
              title={t("publicHolidays.errorTitle")}
              borderStartWidth="3px"
              borderStartColor="red.fg"
            >
              {error}
            </Alert>
          ) : isLoading ? (
            <Stack gap="3">
              <Skeleton height="2.5rem" />
              <Skeleton height="2.5rem" />
              <Skeleton height="2.5rem" />
              <Skeleton height="2.5rem" />
            </Stack>
          ) : data.length === 0 ? (
            <Box py="12" textAlign="center">
              <Text color="fg.muted" fontWeight="medium">
                {t("publicHolidays.empty")}
              </Text>
            </Box>
          ) : (
            <DataTable
              columns={columns}
              data={data}
              tableSize="sm"
              hideColumnsOnXs={["tags"]}
              initialSortByColumn="date"
              initialSortByDesc={false}
            />
          )}
        </SectionPanel>
      </Stack>
    </Content>
  );
};

export default OverviewPublicHoliday;
